import {
  ContractStatuses,
  SettlementDateStatuses,
  VendorBankDetailsStatuses,
} from '@urbanx/agx.campaigns.client';

export interface ContractsDashboardFilterState {
  agencyIds?: string[];
  contractStatuses?: ContractStatuses[];
  settlementDateStatuses?: SettlementDateStatuses[];
  vendorBankDetailsStatuses?: VendorBankDetailsStatuses[];
}

export const contractsDashboardSettlementDateStatuses = [
  SettlementDateStatuses.ThisWeek,
  SettlementDateStatuses.NextWeek,
] as const;

export const defaultContractsDashboardFilterState: ContractsDashboardFilterState = {
  agencyIds: undefined,
  contractStatuses: undefined,
  settlementDateStatuses: undefined,
  vendorBankDetailsStatuses: undefined,
};

const contractsDashboardFiltersKey = 'contractsDashboardFilters';

const isValidEnumArray = <T extends string>(
  values: unknown,
  allowedValues: readonly T[]
): values is T[] =>
  Array.isArray(values) &&
  values.every((value) => typeof value === 'string' && allowedValues.includes(value as T));

export const storeContractsDashboardFilterState = (filters: ContractsDashboardFilterState) => {
  try {
    localStorage.setItem(contractsDashboardFiltersKey, JSON.stringify(filters));
  } catch {
    // Ignore storage failures so the dashboard still works normally.
  }
};

export const createInitialContractsDashboardFilterState = (): ContractsDashboardFilterState => {
  try {
    const storedFilterState = localStorage.getItem(contractsDashboardFiltersKey);

    if (!storedFilterState) return defaultContractsDashboardFilterState;

    const parsed = JSON.parse(storedFilterState) as Partial<ContractsDashboardFilterState> | null;

    if (!parsed) return defaultContractsDashboardFilterState;

    return {
      agencyIds: Array.isArray(parsed.agencyIds)
        ? parsed.agencyIds.filter((value): value is string => typeof value === 'string')
        : defaultContractsDashboardFilterState.agencyIds,
      contractStatuses: isValidEnumArray(parsed.contractStatuses, Object.values(ContractStatuses))
        ? parsed.contractStatuses
        : defaultContractsDashboardFilterState.contractStatuses,
      settlementDateStatuses: isValidEnumArray(
        parsed.settlementDateStatuses,
        contractsDashboardSettlementDateStatuses
      )
        ? parsed.settlementDateStatuses
        : defaultContractsDashboardFilterState.settlementDateStatuses,
      vendorBankDetailsStatuses: isValidEnumArray(
        parsed.vendorBankDetailsStatuses,
        Object.values(VendorBankDetailsStatuses)
      )
        ? parsed.vendorBankDetailsStatuses
        : defaultContractsDashboardFilterState.vendorBankDetailsStatuses,
    };
  } catch {
    return defaultContractsDashboardFilterState;
  }
};
